import { useContext, useEffect, useRef, useState } from "react";
import DynamicForm from "../../../components/DynamicForm/DynamicForm";
import { UserContext } from "../../../context/UserContext";
import { GetSubscriptionTier } from "../../../utils/businessUtils";
import { SettingOption } from "../../../components/SettingOption/setting-option";
import warningIcon from "../../../assets/images/warning.png";
import "./privacy-settings.css";
import { MFAModal } from "../../../components/Modals/MFAModal/mfa-setup-modal";
import { Alert, formatPhoneNumber, SetLocalSetting } from "../../../utils/genericUtils";
import ChangePasswordModal from "../../../components/Modals/ChangePasswordModal/ChangePasswordModal";
import { PATCH, POST, UpdateSetting } from "../../../utils/httpUtils";
import { NotificationsContext } from "../../../context/NotificationsContext";
import { useNavigate } from "react-router-dom";
import { AlertContext } from "../../../context/AlertContext";
import { LoaderContext } from "../../../context/LoaderContext";
import { ConfirmationModal } from "../../../components/Modals/ConfirmationModal/confirmation-modal";
import ThemeContext from "../../../context/ThemeContext";

const defaultProps = {
  mfaEnabled: false,
  allow_public_access: false,
  onChanged: () => {},
};

export default function PrivacySettings(props = defaultProps) {
  const router = useNavigate();

  const { show, showloader, hideloader } = useContext(LoaderContext);
  const { globalAlert } = useContext(AlertContext);

  const { user, loginUser } = useContext(UserContext);

  const { pushNotification } = useContext(NotificationsContext);

  const { setTheme } = useContext(ThemeContext);

  const [publicAccessEnabled, setPublicAccessEnabled] = useState(props.allow_public_access ?? false);

  const [mfaEnabled, setMFAEnabled] = useState(props.mfaEnabled ?? false);

  //modals
  const [mfaSetupModalActive, setMfaSetupModalActive] = useState(false);
  const [changePasswordModalActive, setChangePasswordModalActive] = useState(false);
  const [signOutEverywhereModalActive, setSignOutEverywhereModalActive] = useState(false);

  // const [autoRenew, setAutoRewnew] = useState(true);

  //useEffect(() => {}, [mfaEnabled]);

  const PrivacySettingsFields = [
    {
      id: "stripe-cus-id",
      name: "MFA Type",
      type: "text",
      defaultValue: user.mfaMethod.toUpperCase(),
      disabled: true,
    },
    {
      id: "subscription-plan",
      name: "MFA Method",
      type: "text",
      disabled: true,
      defaultValue: user.mfaMethod === "sms" ? formatPhoneNumber(user.phone) : user.email,
    },
  ];

  function EnablePublicAccess(enabled) {
    setPublicAccessEnabled(enabled);
    UpdateSetting("allow_public_access", enabled, user).then((response) => {
      if (response.success) {
        let message = enabled ? "Public Access enabled" : "Public Access disabled";
        pushNotification("Setting Updated", message);
      } else {
        setPublicAccessEnabled(!enabled);
        pushNotification("Setting Failed To Update", (response.error && response.error.message) ?? "Unknown error occured");
      }
    });
  }

  function DisableMFA() {
    setMFAEnabled(false);
    PATCH("/disable-mfa", null, user).then((response) => {
      if (response.success) {
        let message = "MFA disabled";
        pushNotification("Setting Updated", message);
      } else {
        setMFAEnabled(true);
        pushNotification("Setting Failed To Update", (response.error && response.error.message) ?? "Unknown error occured");
      }
    });
  }

  async function SignOutEveryWhere() {
    showloader();
    let response = await POST("/sign-out-everywhere", undefined, user);
    hideloader();
    if (response.success) {
      localStorage.removeItem("jwt");
      sessionStorage.removeItem("jwt");
      SetLocalSetting("theme", undefined);
      //setTheme("light");
      loginUser(undefined);
      router("/");
    } else {
      Alert(globalAlert, "error", "Failed to Sign Out", response.error.message);
    }
  }

  return (
    <section className="privacy-settings-container">
      {/* Modals */}

      {/* Change Password Modal */}
      <ChangePasswordModal isActive={changePasswordModalActive} onCancel={() => setChangePasswordModalActive(false)} />

      {/* Sign Out Everywhere Confirmation Modal */}
      <ConfirmationModal
        id="sign-out-everywhere-modal"
        icon={warningIcon}
        animate={true}
        showDivider={true}
        isActive={signOutEverywhereModalActive}
        header="Warning"
        message="This will sign you out on this device and all other devices logged into this account."
        confirmText="Sign Out Of All Devices"
        denyText="Stay Logged In"
        denyClass="btn-green"
        confirmClass="btn-red"
        onDeny={() => setSignOutEverywhereModalActive(false)}
        onConfirm={SignOutEveryWhere}
      />

      {/* MFA setup modal */}
      <MFAModal
        id="mfa-modal"
        animate={true}
        isActive={mfaSetupModalActive}
        onCancel={() => {
          setMFAEnabled(false);
          setMfaSetupModalActive(false);
        }}
        onSuccess={() => {
          pushNotification("Setting Updated", "Multi-Factor Authentication enabled");
          setMFAEnabled(true);
          setMfaSetupModalActive(false);
        }}
      />

      {/* Enable Public Objects */}

      <SettingOption
        id={"public-access-toggle"}
        controlType={"toggle"}
        defaultValue={publicAccessEnabled ?? false}
        onChange={(value) => EnablePublicAccess(value)}
        description={"Allow Public Acccess Objects"}
      />

      {/* MFA TOGGLE */}
      <SettingOption
        id={"mfa-toggle"}
        controlType={"toggle"}
        defaultValue={mfaEnabled ?? false}
        onChange={(value) => {
          if (value === true) setMfaSetupModalActive(true);
          setMFAEnabled(value);

          if (value === false) {
            DisableMFA();
          }
        }}
        // only confirm for disable mfa
        useConfirmation={mfaEnabled === true ? true : false}
        confirmationSettings={{
          id: "mfa-toggle",
          icon: warningIcon,
          animate: true,
          header: "Are you sure you want to disable MFA?",
          message: "doing so will make your account less secure!",
          confirmText: "Disable MFA",
          denyText: "Keep MFA Enabled",
          denyClass: "btn-green",
        }}
        description={"Multi-Factor Authentication"}
      />

      {/* MFA details */}
      {mfaEnabled && (
        <div className="privacy-settings-form-container">
          <DynamicForm id={"subscription-settings-form"} layout={[1, 2]} fields={PrivacySettingsFields} />
        </div>
      )}

      {/* Actions */}
      <h3>Actions</h3>
      <div className="setting-option-divider"></div>

      {/* Change PASSWORD */}
      {!user.isOauth && (
        <SettingOption
          id={"change-password"}
          controlType={"button"}
          buttonText="Change Password"
          buttonClass={"btn-green-inverted"}
          description="Change the password on your account, requires entering your current password."
          onClick={() => setChangePasswordModalActive(true)}
          showDivider={true}
        />
      )}

      {/* SIGN Out Of All devices */}
      <SettingOption
        id={"sign-out-everywhere"}
        controlType={"button"}
        buttonText="Sign Out Everywhere"
        buttonClass={"btn-orange-inverted"}
        description="Logs your account out from all devices"
        onClick={() => setSignOutEverywhereModalActive(true)}
        showDivider={false}
      />
    </section>
  );
}
